/**
 * The four numbers on Tracking → Delivery's tile strip, and the rules behind them.
 *
 * Each tile is a whole section compressed to one figure (Theresa, Oct 19 2026): tiles at the top,
 * their detail collapsed at the bottom, click a tile to open its section. So every rule here has
 * to survive being read with no context at all — which is why each returns a `verdict` and a
 * reason in words rather than a bare number somebody has to interpret.
 *
 * ⚠️ ALL OF IT IS JUDGEMENT AND ALL OF IT IS HERE, not in the proxy — the same split the radar's
 * traffic lights take. What counts as a consistent plan, a bad slip or a slowdown changes more
 * often than the Hiring Database does, and it has to be testable without a network.
 */
import type { PlanVsNow, TrackingData } from '../types/taTracking';

/* ------------------------------------------------------------ 1 · planning consistency */

export interface PlanningConsistency {
  /** 0–1. How much of the frozen plan is still the plan. */
  score: number | null;
  plan: number;
  /** Roles the plan DELIVERED. Not churn — this is the plan working. */
  filled: number;
  /** Roles that MOVED the plan rather than delivering it. */
  churn: number;
  /** The biggest single cause, for the tile's one-line note. */
  topCause: { label: string; count: number } | null;
  verdict: 'solid' | 'drifting' | 'recut' | 'unknown';
}

/**
 * ⚠️ `filled` IS NOT CHURN, and that distinction is the whole metric. Every bucket in the waterfall
 * except `filled` is the plan changing shape after it was frozen — pushed, paused, undated,
 * dropped, or arriving late. A trimester that delivered every planned role scores 100%; one that
 * delivered none but left the plan untouched also scores 100%, which is correct: this measures how
 * well the PLAN was made, not how well it was delivered. Progress already measures delivery, one
 * tile over, and merging the two would leave neither answerable.
 */
const CHURN_BUCKETS = [
  { key: 'redated', label: 'pushed to a later month' },
  { key: 'onHold', label: 'put on hold' },
  { key: 'added', label: 'added after the freeze' },
  { key: 'dropped', label: 'dropped' },
  { key: 'undated', label: 'lost their date' },
  { key: 'movedIn', label: 'pulled into the window' },
] as const;

export function planningConsistency(plan: PlanVsNow | null | undefined): PlanningConsistency {
  const empty: PlanningConsistency = {
    score: null,
    plan: 0,
    filled: 0,
    churn: 0,
    topCause: null,
    verdict: 'unknown',
  };
  if (!plan || plan.plan <= 0) return empty;

  const counts = plan.counts;
  const causes = CHURN_BUCKETS.map(({ key, label }) => ({ label, count: counts[key] ?? 0 })).filter(
    (c) => c.count > 0
  );
  const churn = causes.reduce((sum, c) => sum + c.count, 0);

  /*
   * Clamped at zero: more churn than the plan held is possible — a trimester can add more roles
   * than it froze — and a negative "consistency" is not a thing anybody can read.
   */
  const score = Math.max(0, 1 - churn / plan.plan);
  const topCause = causes.length ? causes.reduce((a, b) => (b.count > a.count ? b : a)) : null;

  return {
    score,
    plan: plan.plan,
    filled: counts.filled ?? 0,
    churn,
    topCause,
    verdict: score >= 0.8 ? 'solid' : score >= 0.5 ? 'drifting' : 'recut',
  };
}

/* ------------------------------------------------------------- 2 · start date accuracy */

export interface RoleSlip {
  positionId: string;
  title: string;
  recruiter: string;
  /** Months between the month it was planned for and where it actually sits. */
  slipMonths: number;
  /** Whether somebody has started, or the seat is still open and running late. */
  kind: 'started' | 'open';
  plannedMonth: string;
  actualMonth: string | null;
}

export interface StartAccuracy {
  /** Median slip in months across every role we can measure. */
  medianSlip: number | null;
  onTime: RoleSlip[];
  /** One or two months late. */
  slipped: RoleSlip[];
  /** Three months or more. */
  wayOff: RoleSlip[];
  measured: number;
  verdict: 'on-time' | 'slipping' | 'late' | 'unknown';
}

/** Months between two `YYYY-MM` strings. Null when either is missing or unparseable. */
export function monthsBetween(from: string | null, to: string | null): number | null {
  if (!from || !to) return null;
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  if (!fy || !fm || !ty || !tm) return null;
  return (ty - fy) * 12 + (tm - fm);
}

/** One or two months is a slip; three is a different conversation. */
const WAY_OFF_MONTHS = 3;

/** The HDB's own word, lower-cased by `normaliseStatus` before it ever reaches the browser. */
const isHired = (status: string) => status.toLowerCase() === 'hired';

/**
 * How far starts are running from the month they were planned for.
 *
 * ⚠️ TWO POPULATIONS, ONE FIGURE, and the row says which it is. A role somebody has STARTED has a
 * final answer — it slipped by exactly this much. A role still OPEN and past its month has a slip
 * that is still growing, measured to today. Leaving the open ones out would flatter the number
 * precisely when the trimester is going badly, which is when it is read; merging them silently
 * would present a moving figure as a settled one.
 *
 * ⚠️ MONTHS, NOT WEEKS. The Hiring Database stores a planned START MONTH, so a figure in weeks
 * would be arithmetic on precision the source does not have. The tile says months and the section
 * shows the roles.
 */
export function startAccuracy(
  roles: readonly {
    positionId: string;
    title: string;
    recruiter: string;
    plannedMonth: string | null;
    actualMonth: string | null;
    month: string | null;
    status: string;
  }[],
  thisMonth: string
): StartAccuracy {
  const rows: RoleSlip[] = [];

  for (const r of roles) {
    if (!r.plannedMonth) continue;

    /*
     * ⚠️ A HIRED ROLE WITH NO RECORDED START IS NOT AN OVERDUE SEAT (CodeRabbit, PR #637). Without
     * this it fell to the open branch, was measured against today and counted as late — a role
     * somebody has already filled, reported as the trimester slipping. Its slip is simply unknown
     * until a start date is entered, and unknown is not late.
     */
    const started = Boolean(r.actualMonth);
    if (!started && isHired(r.status)) continue;
    const against = started ? r.actualMonth : thisMonth;
    const slip = monthsBetween(r.plannedMonth, against);
    if (slip == null) continue;

    /*
     * ⚠️ An OPEN role whose month is still ahead is not "on time" — it is not due yet, and has no
     * accuracy to measure. Counting it as on time would let a trimester full of future roles
     * report perfect accuracy on the day it was planned.
     */
    if (!started && slip <= 0) continue;

    rows.push({
      positionId: r.positionId,
      title: r.title,
      recruiter: r.recruiter,
      slipMonths: slip,
      kind: started ? 'started' : 'open',
      plannedMonth: r.plannedMonth,
      actualMonth: r.actualMonth ?? null,
    });
  }

  if (!rows.length) {
    return { medianSlip: null, onTime: [], slipped: [], wayOff: [], measured: 0, verdict: 'unknown' };
  }

  const sorted = [...rows].sort((a, b) => a.slipMonths - b.slipMonths);
  const mid = Math.floor(sorted.length / 2);
  const medianSlip =
    sorted.length % 2 ? sorted[mid].slipMonths : (sorted[mid - 1].slipMonths + sorted[mid].slipMonths) / 2;

  const byLateness = [...rows].sort((a, b) => b.slipMonths - a.slipMonths);
  return {
    medianSlip,
    onTime: byLateness.filter((r) => r.slipMonths <= 0),
    slipped: byLateness.filter((r) => r.slipMonths > 0 && r.slipMonths < WAY_OFF_MONTHS),
    wayOff: byLateness.filter((r) => r.slipMonths >= WAY_OFF_MONTHS),
    measured: rows.length,
    verdict: medianSlip <= 0 ? 'on-time' : medianSlip < WAY_OFF_MONTHS ? 'slipping' : 'late',
  };
}

/* ----------------------------------------------------------------- the cluster tile */

export interface ClusterSpread {
  /** The cluster furthest BEHIND the calendar, which is the one worth naming. */
  worst: { key: string; progress: number } | null;
  best: { key: string; progress: number } | null;
  /** How many clusters are at or ahead of the expected pace. */
  onPace: number;
  total: number;
}

/**
 * The cluster split, compressed to one tile (Theresa, Oct 19 2026: it "fits into one tile easily").
 *
 * ⚠️ It names the cluster furthest BEHIND, not the company average. Averaging three clusters is
 * the mistake the capacity model spent a release removing: a company on pace with one cluster at
 * 40% is not a company on pace. The detail — every cluster's bar — is the section underneath.
 */
export function clusterSpread(
  clusters: readonly { key: string; hired: number; target: number }[],
  expectedPace: number
): ClusterSpread {
  const rated = clusters
    .filter((c) => c.target > 0)
    .map((c) => ({ key: c.key, progress: c.hired / c.target }));

  // ⚠️ `total` is how many clusters can be MEASURED, not how many exist — "0/3 on pace" when none
  // of the three has a target reads as three clusters failing rather than as nothing to measure.
  if (!rated.length) return { worst: null, best: null, onPace: 0, total: 0 };

  return {
    worst: rated.reduce((a, b) => (b.progress < a.progress ? b : a)),
    best: rated.reduce((a, b) => (b.progress > a.progress ? b : a)),
    onPace: rated.filter((c) => c.progress >= expectedPace).length,
    total: rated.length,
  };
}

/** Everything the tile strip needs, from the payload the tab already has. */
export function deliveryTiles(data: TrackingData, thisMonth: string) {
  return {
    consistency: planningConsistency(data.planVsNow),
    clusters: clusterSpread(data.clusters, data.pace.share),
    thisMonth,
  };
}
